/**
 * "More time. For what matters." campaign content. Each story pairs a film with the conversation Chat.co
 * was answering while that person was away (the same conversation the film shows at the dark desk).
 * All businesses in the conversations are fictional. `{1}` marks a citation; lines starting with `•` are bullets.
 */
export interface Story {
  id: string;
  number: string;
  line: string;
  story: string;
  videoId: string;
  image: string;
  /** object-position for the full-bleed still, so the subject survives portrait crops. */
  focus: string;
  meanwhile: {
    channel: string;
    business: string;
    asker: string;
    question: string;
    answer: string[];
    source: string;
    lang?: string;
  };
}

export const FILM = {
  videoId: 'pSYO4JNFdKU',
  title: 'More time. For what matters. (60 seconds)',
  image: '/more-time/hero.jpg',
};

export const PLAYLIST_URL = 'https://www.youtube.com/playlist?list=PLOrnr89rtC5I';
export const SHORTS_URL = 'https://www.youtube.com/playlist?list=PLND0TS9GOb5k';

export const STORIES: Story[] = [
  {
    id: 'music',
    number: '01',
    line: 'For the music.',
    story:
      'An empty hall, a grand piano, and the Rachmaninoff she has been working toward for years.',
    videoId: 'BXq6d0-VXVY',
    image: '/more-time/pianist.jpg',
    focus: '32% 50%',
    meanwhile: {
      channel: 'Website',
      business: 'Calder & Wynn Law',
      asker: 'A client',
      question: 'Can I move my consultation to next week?',
      answer: [
        'Of course.',
        '•You can reschedule at no charge up to 24 hours before your appointment {1}',
        '•Pick any open time, or reply with a day that suits you.',
      ],
      source: 'Client Scheduling Policy.pdf',
    },
  },
  {
    id: 'quiet',
    number: '02',
    line: 'For the quiet.',
    story:
      'A morning walk into the rainforest, until a blue morpho decides she is worth a closer look.',
    videoId: 'dhBi17iYcIM',
    image: '/more-time/forest.jpg',
    focus: '50% 40%',
    meanwhile: {
      channel: 'Website',
      business: 'Verdant Trails',
      asker: 'A traveler',
      question: 'What’s your cancellation policy for the rainforest tour?',
      answer: [
        'Cancel up to 48 hours before your tour for a full refund {1}.',
        'Inside 48 hours, we’ll gladly move you to another date at no cost {1}.',
      ],
      source: 'Booking & Cancellation Policy.pdf',
    },
  },
  {
    id: 'race',
    number: '03',
    line: 'For the race.',
    story: 'Five red lights, then nothing but the track and the last corner before the flag.',
    videoId: 'coTa7MQDaZU',
    image: '/more-time/race.jpg',
    focus: '55% 50%',
    meanwhile: {
      channel: 'Website',
      business: 'Northline Motors',
      asker: 'A customer',
      question: 'Does the extended warranty cover brake repairs?',
      answer: [
        'Pads and rotors are wear items, so they aren’t covered {1}.',
        'The brake system itself (calipers, lines and ABS) is covered for 5 years {1}.',
      ],
      source: 'Extended Warranty Terms.pdf',
    },
  },
  {
    id: 'canvas',
    number: '04',
    line: 'For the canvas.',
    story:
      'Blue, then gold. The sea she has been painting in her head for months, finally on canvas.',
    videoId: 'EbHDS_kpSgc',
    image: '/more-time/painter.jpg',
    focus: '35% 45%',
    meanwhile: {
      channel: 'Website · en español',
      business: 'Taller Luz',
      asker: 'Una clienta',
      question: '¿Hacen envíos a Puerto Rico? ¿Cuánto tardan?',
      answer: [
        '¡Sí, enviamos a Puerto Rico! {1}',
        '•Preparamos tu pedido en 1–2 días hábiles',
        '•La entrega toma de 4 a 6 días hábiles {1}',
        '•Envío gratis en compras de más de $75',
      ],
      source: 'Política de envíos.pdf',
      lang: 'es',
    },
  },
  {
    id: 'waves',
    number: '05',
    line: 'For the waves.',
    story: 'First light, an empty lineup, and one wave worth the wait.',
    videoId: 'TkRD8o-uGZ8',
    image: '/more-time/surfer.jpg',
    focus: '62% 50%',
    meanwhile: {
      channel: 'WhatsApp',
      business: 'Swell Surf School',
      asker: 'Alex M.',
      question: 'Do your lessons include the board and wetsuit?',
      answer: [
        'Yes! Every lesson includes a soft-top board and a wetsuit {1}.',
        'Just bring a towel and sunscreen.',
      ],
      source: 'Lesson FAQ.pdf',
    },
  },
  {
    id: 'them',
    number: '06',
    line: 'For them.',
    story: 'A dock, a lake at sunset, and a daughter who wants to know what that bird is called.',
    videoId: 'QGqwqEqV4uk',
    image: '/more-time/father.jpg',
    focus: '60% 45%',
    meanwhile: {
      channel: 'Website',
      business: 'Harbor Lane Accounting',
      asker: 'A client',
      question: 'What documents do I need for my tax return?',
      answer: [
        'Here’s what to bring:',
        '•W-2s and 1099s from every employer {1}',
        '•Last year’s tax return',
        '•Receipts for deductions, like childcare and donations {1}',
      ],
      source: '2026 Tax Prep Checklist.pdf',
    },
  },
  {
    id: 'kitchen',
    number: '07',
    line: 'For the kitchen.',
    story: 'Flour, water, and time. Bread made the slow way, with nowhere else to be.',
    videoId: 'nb6Z70w0Q3s',
    image: '/more-time/chef.jpg',
    focus: '58% 45%',
    meanwhile: {
      channel: 'Website',
      business: 'Osteria Fiora',
      asker: 'A guest',
      question: 'Do you have gluten-free options?',
      answer: [
        'Yes!',
        '•Gluten-free pasta is available for any of our pastas {1}',
        '•Our risotto and grilled fish are naturally gluten-free {1}',
      ],
      source: 'Dinner Menu & Allergen Guide.pdf',
    },
  },
  {
    id: 'stars',
    number: '08',
    line: 'For the stars.',
    story:
      'A blanket in a dark field, the Milky Way overhead, and a shooting star nobody else saw.',
    videoId: 'O30BMU3TjF0',
    image: '/more-time/stargazer.jpg',
    focus: '55% 50%',
    meanwhile: {
      channel: 'Internal knowledge',
      business: 'Lumen Labs',
      asker: 'Maya, a new hire',
      question: 'How many PTO days do I get in my first year?',
      answer: [
        'You get 18 PTO days in your first year, plus 10 company holidays {1}.',
        '•Request time off in the HR portal',
        '•Up to 5 unused days roll over to next year {1}',
      ],
      source: 'Employee Handbook 2026.pdf',
    },
  },
  {
    id: 'ride',
    number: '09',
    line: 'For the ride.',
    story: 'The long climb, until the road breaks through the clouds and the light comes in.',
    videoId: 'w1ZuH2cA4iM',
    image: '/more-time/cyclist.jpg',
    focus: '45% 50%',
    meanwhile: {
      channel: 'Website',
      business: 'Bayside Physical Therapy',
      asker: 'A new patient',
      question: 'Do you take my insurance? I’d like to book a first visit.',
      answer: [
        'We’re in-network with most major plans {1}.',
        'I’ve passed your request to our care coordinator, who will confirm your coverage and book your first visit.',
      ],
      source: 'Lead captured · New patient',
    },
  },
  {
    id: 'story',
    number: '10',
    line: 'For the story.',
    story: 'Rain on the window, tea going cold, and the last chapter she has been saving.',
    videoId: 'oH7vOK5FvnU',
    image: '/more-time/reader.jpg',
    focus: '62% 40%',
    meanwhile: {
      channel: 'Website · 11:48 PM',
      business: 'Paper Lantern Books',
      asker: 'A reader',
      question: 'When will my order arrive?',
      answer: [
        'Orders ship within 1 business day, and standard delivery takes 3–5 days {1}.',
        'You’ll get a tracking link by email as soon as it ships {1}.',
      ],
      source: 'Shipping & Delivery.pdf',
    },
  },
];
